'use client'

import { Card } from '../ui/card'
import { HotelWithTrustYou } from '@/app/(frontend)/[lang]/hotels/[country]/[city]/CityClient'
import ImageCarousel from './ImageCarousel'
import { MapPin } from 'lucide-react'
import IconBadge from './IconBadge'
import { IconName } from 'lucide-react/dynamic'
import { buttonVariants } from '../ui/button-variants'
import Star from '@/public/illustrations/star.svg'
import hotelTitle from '@/lib/helpers/hotelName'
import { useSearchParams } from 'next/navigation'
import { Link } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import { formatNumber } from '@/utilities/formatNumber'
import type { AvailabilityOverviewHotel } from '@/lib/booking-engine/types'

const HotelCard = ({
  hotel,
  country,
  city,
  price,
  nights,
}: {
  hotel: HotelWithTrustYou
  country: string
  city: { slug: string; title: string }
  /** Undefined while availability is still loading, null once it failed. */
  price?: AvailabilityOverviewHotel | null
  /** What the quoted prices cover. Absent alongside a missing price. */
  nights?: number
}) => {
  const searchParams = useSearchParams()

  const t = useTranslations()

  if (!hotel.slug) return null

  return (
    <Card className='bg-card max-w-lg gap-0 overflow-hidden rounded-xl border pt-0 md:rounded-3xl'>
      {hotel.images && hotel.images.length > 0 ? (
        <ImageCarousel media={hotel.images} />
      ) : (
        <div className='aspect-16/9 w-full bg-stone-300' />
      )}

      <Link
        href={{
          pathname: '/hotels/[country]/[city]/[hotel]',
          params: { country, city: city.slug, hotel: hotel.slug },
          query: Object.fromEntries(searchParams),
        }}
        className='flex h-full w-full'
      >
        <div className='flex w-full flex-col justify-between space-y-4 px-4 pt-4 md:cursor-pointer'>
          <div className='space-y-2'>
            <h3 className='text-2xl'>{hotelTitle(hotel.title, hotel.hotelType)}</h3>
            <div className='flex space-x-4'>
              {hotel?.trustYou && (
                <div className='flex items-center space-x-2'>
                  <Star className='size-5' />

                  <div className='flex items-end'>
                    <p>{hotel?.trustYou.response.transformedScore}</p>
                    <p className='text-muted-foreground text-sm'>/5</p>
                  </div>
                </div>
              )}

              <div className='text-muted-foreground flex items-center space-x-1'>
                <MapPin className='size-5' strokeWidth={1.5} />
                <p>{city.title}</p>
              </div>
            </div>
          </div>

          {hotel.amenities &&
            hotel.amenities.length > 0 &&
            hotel.amenities.slice(0, 3).map((amenity) => {
              if (typeof amenity.icon === 'number') return null

              return (
                <div key={amenity.id} className='flex items-center space-x-2'>
                  <IconBadge dynamic={amenity.icon.icon as IconName} />
                  <p>{amenity.icon.title}</p>
                </div>
              )
            })}

          <div className='w-full justify-end space-y-2'>
            {price && <PriceLine price={price} nights={nights} />}
            {/* span, not <button>: the whole card is already a link */}
            <span
              className={buttonVariants({
                variant: 'secondary',
                className: 'h-12 w-full rounded-full',
              })}
            >
              {t('select_room')}
            </span>
          </div>
        </div>
      </Link>
    </Card>
  )
}

/** Kept identical across every branch so cards in a row stay aligned. */
const PRICE_SLOT = 'min-h-14'

/**
 * Only 'sold-out' is a claim about availability. A hotel that isn't on the new
 * booking engine — or that we simply failed to price — still books fine through
 * its own page, so it shows no price rather than a message that would talk the
 * guest out of a booking we can take.
 */
const PriceLine = ({
  price,
  nights,
}: {
  price?: AvailabilityOverviewHotel | null
  nights?: number
}) => {
  const t = useTranslations()

  if (price === undefined) {
    return (
      <div className={`${PRICE_SLOT} space-y-2 pt-1`}>
        <div className='h-4 w-24 animate-pulse rounded bg-stone-200' />
        <div className='h-7 w-40 animate-pulse rounded bg-stone-200' />
      </div>
    )
  }

  if (price?.status === 'sold-out') {
    return <p className={`${PRICE_SLOT} text-muted-foreground flex items-end`}>{t('sold_out')}</p>
  }

  if (price?.status !== 'available' || price.from === null || price.fromPerNight === null) {
    return <div className={PRICE_SLOT} />
  }

  return (
    <div className={PRICE_SLOT}>
      {nights !== undefined && (
        <p className='text-muted-foreground'>{t('nights_from', { count: nights })}</p>
      )}

      <p className='flex flex-wrap items-baseline gap-x-2'>
        <span className='text-2xl'>
          {formatNumber(price.from)} {price.currency}
        </span>
        <span className='text-muted-foreground lowercase'>{t('total')}</span>
        <span className='text-muted-foreground' aria-hidden='true'>
          &bull;
        </span>
        <span className='text-muted-foreground underline'>
          {formatNumber(price.fromPerNight)} {price.currency}/
          <span className='lowercase'>{t('night')}</span>
        </span>
      </p>
    </div>
  )
}

export default HotelCard
